"use client";
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import Image from "next/image";
import { SearchCategories, SearchMerchant } from '@/services/dataTypes';
import { keywordsAction, searchAction } from '@/app/actions';
import { getCategoryHref, getMerchantHref } from '@/constants/hooks';
import { FontAwesomeIcon } from '@/constants/icons';
import { faStore, faTags, faBoxOpen, faSearch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

interface Props {
  companyId: string;
  mer_slug: string;
  slug_type: string;
  cat_slug: string;
}

const SearchBar = ({ companyId, mer_slug, slug_type, cat_slug }: Props) => {
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams);
  const [search, setSearch] = useState<string>(params.get('query') || "");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [tagsData, setTagsData] = useState<any>([]);
  const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([]);
  const [merchantData, setMerchantData] = useState<SearchMerchant[]>([]);
  const [errMsg, setErrMessage] = useState({ show: false, err: false, msg: '' });
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const pathName = usePathname();
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);

  const handleKeywords = async () => {
    try {
      const response = await keywordsAction(companyId);
      if (response?.data && Array.isArray(response.data)) {
        setTagsData(response.data);
      }
    } catch (e) {
      console.error("Error loading keywords:", e);
    }
  };

  useEffect(() => { handleKeywords(); }, [companyId]);

  const handleSearch = async (searchTerm: string) => {
    try {
      if (searchTerm.trim().length >= 3) {
        setLoading(true);
        const response = await searchAction(searchTerm, companyId);
        if (response?.data?.merchants?.length > 0 || response?.data?.categories?.length > 0) {
          setCategoriesData(response.data?.categories || []);
          setMerchantData(response.data?.merchants || []);
          setIsDropdownVisible(!!(pathName !== '/search'));
          setErrMessage({ show: false, err: false, msg: '' });
        } else {
          setMerchantData([]);
          setCategoriesData([]);
          setErrMessage({ show: true, err: true, msg: 'No results found. Try a different keyword.' });
        }
      } else {
        setMerchantData([]);
        setCategoriesData([]);
        if (searchTerm.trim().length > 0) {
          setErrMessage({ show: true, err: false, msg: 'Type at least 3 characters to search...' });
        } else {
          setErrMessage({ show: false, err: false, msg: '' });
        }
      }
    } catch {
      setErrMessage({ show: true, err: true, msg: 'Something went wrong. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (search.trim() === "") {
        setMerchantData([]);
        setCategoriesData([]);
        setErrMessage({ show: false, err: false, msg: '' });
      } else {
        handleSearch(search);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search, companyId]);

  useEffect(() => {
    const query = params.get('query') || "";
    if (query !== search && document.activeElement !== inputRef.current) {
      setSearch(query);
    }
  }, [searchParams]);

  useEffect(() => {
    if (pathName === '/search') setIsDropdownVisible(false);
  }, [pathName]);

  const submitSearchNow = () => {
    if (search.trim()) {
      router.push(`/search?query=${encodeURIComponent(search.trim())}`);
      setIsDropdownVisible(false);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchWrapperRef.current &&
        !searchWrapperRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Premium Animated Placeholders
  const placeholderTexts = [
    "Search for your favorite stores...",
    "Find exclusive discount codes...",
    "Discover today's best deals...",
    "Explore popular categories...",
    "Search brands & merchants..."
  ];
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prev) => (prev + 1) % placeholderTexts.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // High-end Fallback keywords if API has no loaded keywords
  const defaultTags = ["Amazon", "Walmart", "Target", "Nike", "Macy's", "Adidas", "Sephora", "eBay"];
  const activeTags = tagsData && tagsData.length > 0 ? tagsData : defaultTags;

  // Visual condition for showing the dropdown container
  const shouldShowDropdown = isDropdownVisible && (search.trim().length > 0 || activeTags.length > 0);

  return (
    <div ref={searchWrapperRef} className="relative w-full" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <div className={`
        relative flex items-center rounded-2xl p-1.5
        bg-white border transition-all duration-300 w-full z-10
        ${isFocused
          ? 'border-[#5D27A1] shadow-[0_0_0_4px_rgba(74,154,110,0.15)]'
          : 'border-slate-200 hover:border-slate-300 shadow-sm hover:shadow'
        }
      `}>
        {/* Soft Breathing Focus Glow Ring */}
        {isFocused && (
          <div className="absolute inset-0 rounded-2xl pointer-events-none animate-pulse-ring" />
        )}

        {/* Clean left spacing and focus state search icon indicator */}
        <div className="pl-4 pr-2 text-slate-400">
          <FontAwesomeIcon icon={faSearch} className={`w-4 h-4 transition-colors duration-300 ${isFocused ? 'text-[#5D27A1]' : ''}`} />
        </div>

        <input
          type="text"
          placeholder={placeholderTexts[placeholderIndex]}
          className="bg-transparent border-none outline-none text-[14px] py-2 w-full text-slate-800 placeholder:text-slate-400 font-medium z-10 transition-all duration-500"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
          ref={inputRef}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setIsDropdownVisible(true);
          }}
          onFocus={() => {
            setIsFocused(true);
            setIsDropdownVisible(true);
          }}
          onBlur={() => setIsFocused(false)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && search.trim()) {
              submitSearchNow();
            }
          }}
          aria-label="Search"
          spellCheck="false"
          autoComplete="off"
        />

        {/* Elegant search icon trigger button */}
        <button
          className={`
            relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 focus:outline-none shrink-0 z-10
            ${search.trim().length > 0
              ? 'bg-[#5D27A1] hover:bg-[#471d7c] text-white shadow-md shadow-[#5D27A1]/30 scale-100 opacity-100 cursor-pointer'
              : 'bg-slate-50 text-slate-300 pointer-events-none scale-95 opacity-50'
            }
          `}
          onClick={submitSearchNow}
          aria-label="Submit search"
        >
          <FontAwesomeIcon icon={faSearch} className="w-3.5 h-3.5" />
        </button>

        {/* Redesigned Premium Search Dropdown Menu */}
        {shouldShowDropdown && (
          <div className="absolute left-0 right-0 sm:right-auto top-full mt-3 w-full sm:w-[480px] md:w-[540px] opacity-100 visible transition-all duration-200 z-[99999] animate-slideDown origin-top">
            <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-5 max-h-[75vh] overflow-y-auto custom-scrollbar relative" style={{ backgroundColor: '#FFFFFF' }}>

              {/* Soft internal brand glow for premium UI depth */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#5D27A1]/5 rounded-full blur-3xl pointer-events-none opacity-60" />

              {loading && search.trim().length >= 3 && merchantData.length === 0 && categoriesData.length === 0 && !errMsg.show && (
                <div className="text-center text-[13px] py-8 text-[#5D27A1] font-semibold flex flex-col items-center justify-center gap-2 animate-pulse">
                  <div className="flex gap-1.5 mb-1">
                    <span className="w-2.5 h-2.5 bg-[#5D27A1]/50 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2.5 h-2.5 bg-[#5D27A1]/80 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2.5 h-2.5 bg-[#5D27A1] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <span className="tracking-widest uppercase text-[9px] text-[#5D27A1]/70 mt-1">Discovering best deals...</span>
                </div>
              )}

              {errMsg.show && search.trim().length > 0 && !loading && (
                <div className="py-5 text-center animate-fadeInUp flex items-center justify-center gap-2 bg-slate-50/50 rounded-xl border border-slate-100 p-4 mb-4">
                  <FontAwesomeIcon icon={faInfoCircle} className={`w-4 h-4 ${errMsg.err ? 'text-rose-500' : 'text-[#5D27A1]'}`} />
                  <p className={`text-[13px] font-semibold ${errMsg.err ? 'text-rose-600' : 'text-slate-600'}`}>
                    {errMsg.msg}
                  </p>
                </div>
              )}

              {/* Stores / Merchants - Dynamic Search Results */}
              {merchantData.length > 0 && !loading && (
                <div className="mb-6 animate-fadeInUp relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-[#5D27A1]/5">
                      <FontAwesomeIcon icon={faStore} className="text-[#5D27A1] w-3 h-3" />
                    </div>
                    <p className="text-[12px] font-bold tracking-widest text-slate-700 uppercase">
                      Featured Stores
                    </p>
                  </div>
                  <div className="grid grid-cols-4 gap-2.5">
                    {merchantData.slice(0, 8).map((merchant) => (
                      <Link
                        key={merchant.merchant_name}
                        href={getMerchantHref<SearchMerchant>(merchant, mer_slug, slug_type)}
                        className="group flex flex-col items-center justify-center p-2 rounded-xl bg-white hover:bg-[#5D27A1]/5 border border-slate-100 hover:border-[#5D27A1]/20 shadow-sm hover:shadow transition-all duration-300 aspect-square"
                      >
                        <div className="h-[40px] w-full flex items-center justify-center mb-1.5 relative">
                          {merchant.merchant_logo ? (
                            <Image
                              src={merchant.merchant_logo}
                              alt={merchant.merchant_name}
                              width={60}
                              height={30}
                              style={{ objectFit: "contain" }}
                              className="transition-transform duration-500 group-hover:scale-105"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-slate-50 flex items-center justify-center border border-slate-100">
                              <span className="text-slate-400 text-[8px] font-bold uppercase">Logo</span>
                            </div>
                          )}
                        </div>
                        <div className="text-center w-full px-1">
                          <strong className="text-[10px] font-bold text-slate-600 group-hover:text-[#5D27A1] line-clamp-2 leading-tight transition-colors">
                            {merchant.merchant_name}
                          </strong>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories - Dynamic Search Results */}
              {categoriesData.length > 0 && !loading && (
                <div className="mb-6 animate-fadeInUp relative z-10" style={{ animationDelay: '50ms' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-[#5D27A1]/5">
                      <FontAwesomeIcon icon={faBoxOpen} className="text-[#5D27A1] w-3 h-3" />
                    </div>
                    <p className="text-[12px] font-bold tracking-widest text-slate-700 uppercase">
                      Explore Categories
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {categoriesData.slice(0, 8).map((category) => (
                      <Link
                        key={category?.name}
                        href={getCategoryHref<SearchCategories>(category, cat_slug, slug_type)}
                        className="inline-flex no-underline group"
                      >
                        <div className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200/80 shadow-sm group-hover:bg-[#5D27A1] group-hover:border-[#5D27A1] group-hover:shadow-md group-hover:shadow-[#5D27A1]/20 transition-all duration-300">
                          <span className="text-[11px] font-bold text-slate-600 group-hover:text-white transition-colors">
                            {category?.name}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Tags / Trending Searches (Shown initially on click/focus) */}
              {activeTags.length > 0 && search.trim().length === 0 && (
                <div className="mb-1 animate-fadeInUp relative z-10">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded flex items-center justify-center bg-slate-100">
                      <FontAwesomeIcon icon={faTags} className="text-slate-500 w-3 h-3" />
                    </div>
                    <p className="text-[12px] font-bold tracking-widest text-slate-700 uppercase">
                      Trending Searches
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {activeTags.map((item: string, i: number) => (
                      <Link
                        key={i}
                        href={`/search?query=${encodeURIComponent(item)}`}
                        className="px-4 py-1.5 bg-slate-50 text-slate-600 border border-slate-200/60 text-[12px] font-bold rounded-full hover:bg-[#5D27A1] hover:text-white hover:border-[#5D27A1] transition-all duration-300 shadow-sm hover:shadow"
                      >
                        {item}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-5 pt-3.5 border-t border-slate-100 flex items-center justify-start gap-1.5 text-slate-400">
                    <FontAwesomeIcon icon={faInfoCircle} className="w-3.5 h-3.5 text-[#5D27A1]/60" />
                    <span className="text-[11px] font-medium text-slate-500">
                      Type 3 or more characters to view active merchants and categories.
                    </span>
                  </div>
                </div>
              )}

              {/* View All Results Button (Shown when user has entered search characters) */}
              {search.trim().length >= 3 && !loading && (merchantData.length > 0 || categoriesData.length > 0) && (
                <div className="mt-4 pt-4 border-t border-slate-100 text-center animate-fadeInUp relative z-10">
                  <button
                    onClick={submitSearchNow}
                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-[#5D27A1] text-[12px] font-bold text-white hover:bg-[#471d7c] hover:shadow-lg hover:shadow-[#5D27A1]/20 transition-all duration-300 group/btn"
                  >
                    View All Results
                    <FontAwesomeIcon icon={faSearch} className="w-3 h-3 transform scale-90" />
                  </button>
                </div>
              )}

            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default SearchBar;